// Cache service using IndexedDB
const DB_NAME = 'MediaCache'
const STORE_NAME = 'mediaFiles'
const DB_VERSION = 1
const MAX_CACHE_ENTRIES = 5

const promisifyRequest = (request) => {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'url' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

const withStore = async (mode, callback) => {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode)
    const store = tx.objectStore(STORE_NAME)

    callback(store)

    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

export const saveBlob = async (url, blob) => {
  const timestamp = Date.now()

  await withStore('readwrite', (store) => {
    store.put({ url, blob, timestamp })
  })

  await trimToMaxEntries(MAX_CACHE_ENTRIES)
}

export const getBlob = async (url) => {
  if (!url) {
    console.warn('getBlob: url is missing')
    return null
  }

  const db = await openDB()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const store = tx.objectStore(STORE_NAME)
  const request = store.get(url)
  const result = await promisifyRequest(request)
  return result ? result.blob : null
}

const trimToMaxEntries = async (maxEntries) => {
  const db = await openDB()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)

  const allItems = await promisifyRequest(store.getAll())

  if (allItems.length <= maxEntries) {
    return
  }

  const sorted = allItems.sort((a, b) => a.timestamp - b.timestamp)
  const excessItems = sorted.slice(0, allItems.length - maxEntries)

  for (const item of excessItems) {
    store.delete(item.url)
  }

  return promisifyRequest(tx.done || tx.complete)
}

export const clearAll = () => {
  return withStore('readwrite', (store) => {
    store.clear()
  })
}

export const getAllBlobs = async () => {
  const db = await openDB()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const store = tx.objectStore(STORE_NAME)
  const result = await promisifyRequest(store.getAll())
  return result
}

export const fetchAndCacheBlob = async (url) => {
  const cachedBlob = await getBlob(url)
  if (cachedBlob) {
    console.log('Serving from cache blob:', url)
    return cachedBlob
  }

  const response = await fetch(url)
  const blob = await response.blob()
  await saveBlob(url, blob)
  return blob
}
